import {
  formatAppointmentDate,
  formatNotificationCurrency,
  formatNotificationDateTimeShort,
  formatSessionRemainder,
  normalizeNotificationLabel,
  toNameInitials,
} from "@/lib/notificationMessages/formatters";

export type NotificationMessagePayload = { message: string; action: string };

export function buildLoginMessage(params: { userName: string; dateTime?: Date }): NotificationMessagePayload {
  const userName = normalizeNotificationLabel(params.userName, "Usuário");
  return {
    message: `${userName} — login realizado em ${formatNotificationDateTimeShort(params.dateTime)}`,
    action: "Acesso realizado",
  };
}

export function buildLogoutMessage(params: { userName: string; dateTime?: Date }): NotificationMessagePayload {
  const userName = normalizeNotificationLabel(params.userName, "Usuário");
  return {
    message: `${userName} — logout realizado em ${formatNotificationDateTimeShort(params.dateTime)}`,
    action: "Saída realizada",
  };
}

export function buildAgendamentoCriadoMessage(params: { paciente: string; servico: string; data: string; hora: string; profissional?: string }): NotificationMessagePayload {
  const patient = normalizeNotificationLabel(params.paciente, "Paciente");
  const service = normalizeNotificationLabel(params.servico, "Serviço não informado");
  const professional = params.profissional?.trim() ? ` com ${params.profissional.trim()}` : "";
  return {
    message: `${patient} — ${service} em ${formatAppointmentDate(params.data)} às ${params.hora}${professional}`,
    action: "Agendamento criado",
  };
}

export function buildAgendamentoCanceladoMessage(params: { paciente: string; servico: string; data: string; motivo?: string }): NotificationMessagePayload {
  const patient = normalizeNotificationLabel(params.paciente, "Paciente");
  const service = normalizeNotificationLabel(params.servico, "Serviço não informado");
  const reason = params.motivo?.trim() ? ` Motivo: ${params.motivo.trim()}.` : "";
  return {
    message: `${patient} — ${service} em ${formatAppointmentDate(params.data)} cancelado.${reason}`,
    action: "Agendamento cancelado",
  };
}

export function buildAgendamentoEditadoMessage(params: { paciente: string; servico: string; dataAnterior: string; dataNova: string; horaAnterior?: string; horaNova?: string }): NotificationMessagePayload {
  const patient = normalizeNotificationLabel(params.paciente, "Paciente");
  const service = normalizeNotificationLabel(params.servico, "Serviço não informado");
  const previousDate = formatAppointmentDate(params.dataAnterior);
  const nextDate = formatAppointmentDate(params.dataNova);
  const previousTime = params.horaAnterior?.trim() ? ` às ${params.horaAnterior.trim()}` : "";
  const nextTime = params.horaNova?.trim() ? ` às ${params.horaNova.trim()}` : "";

  return {
    message: `${patient} — ${service} reagendado de ${previousDate}${previousTime} para ${nextDate}${nextTime}`,
    action: "Agendamento editado",
  };
}

export function buildAgendamentoStatusAlteradoMessage(params: { paciente: string; servico: string; data: string; statusAnterior: string; statusNovo: string }): NotificationMessagePayload {
  const patient = normalizeNotificationLabel(params.paciente, "Paciente");
  const service = normalizeNotificationLabel(params.servico, "Serviço não informado");
  return {
    message: `${patient} — ${service} em ${formatAppointmentDate(params.data)} (${params.statusAnterior} → ${params.statusNovo})`,
    action: "Status alterado",
  };
}

export function buildEstoqueBaixoMessage(params: { item: string; quantidadeAtual: number; quantidadeMinima: number }): NotificationMessagePayload {
  const item = normalizeNotificationLabel(params.item, "Item");
  return {
    message: `${item} — estoque em ${params.quantidadeAtual} unidade(s), mínimo de ${params.quantidadeMinima}`,
    action: "Estoque atualizado",
  };
}

export function buildPacienteCadastradoMessage(params: { paciente: string; executor?: string }): NotificationMessagePayload {
  const patient = normalizeNotificationLabel(params.paciente, "Paciente");
  const executor = params.executor?.trim() ? ` por ${params.executor.trim()}` : "";
  return {
    message: `${patient} — cadastro realizado${executor}`,
    action: "Paciente cadastrado",
  };
}

export function buildPagamentoRegistradoMessage(params: { paciente: string; valor: number; servico?: string }): NotificationMessagePayload {
  const patient = normalizeNotificationLabel(params.paciente, "Paciente");
  const service = params.servico?.trim() ? ` referente a ${params.servico.trim()}` : "";
  return {
    message: `${patient} — pagamento de ${formatNotificationCurrency(params.valor)}${service}`,
    action: "Pagamento registrado",
  };
}

export function buildAvaliacaoFisioMessage(params: { paciente: string; profissional?: string }): NotificationMessagePayload {
  const patient = normalizeNotificationLabel(params.paciente, "Paciente");
  const professional = params.profissional?.trim() ? ` por ${params.profissional.trim()}` : "";
  return {
    message: `${patient} — avaliação fisioterapêutica registrada${professional}`,
    action: "Avaliação registrada",
  };
}

export function buildMensagemChatMessage(params: { remetente: string; texto: string }): NotificationMessagePayload {
  const sender = toNameInitials(params.remetente);
  const content = params.texto.trim() || "mensagem sem conteúdo";
  return {
    message: `${sender} — ${content}`,
    action: "Mensagem enviada",
  };
}

export function buildPacoteProximoFimMessage(params: { paciente: string; sessoesRestantes: number }): NotificationMessagePayload {
  const patient = normalizeNotificationLabel(params.paciente, "Paciente");
  return {
    message: `${patient} — ${formatSessionRemainder(params.sessoesRestantes)}`,
    action: "Limite do pacote identificado",
  };
}

export function buildMensalidadeProximaFimMessage(params: { paciente: string; sessoesRestantes: number }): NotificationMessagePayload {
  const patient = normalizeNotificationLabel(params.paciente, "Paciente");
  return {
    message: `${patient} — ${formatSessionRemainder(params.sessoesRestantes)}`,
    action: "Limite da mensalidade identificado",
  };
}
